import { useLayoutEffect } from 'react';

import {
    createBatchedMeshSet,
    createNavigatorBatchedMeshSet
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface BatchedMeshInitializationProps {
    isMainCanvas: boolean;
}

/**
 * Initializes and manages the lifecycle of batched meshes for either the main or navigaton scene.
 */
export const useBatchedMeshInitialization = ({ isMainCanvas }: BatchedMeshInitializationProps) => {
    const setMeshes = usePlacementStore((state) =>
        isMainCanvas ? state.setBatchedMeshes : state.setNavigatorBatchedMeshes
    );

    useLayoutEffect(() => {
        const state = usePlacementStore.getState();
        const quality = state.graphicsSettings.materialQuality;
        const meshSet = isMainCanvas ? createBatchedMeshSet(quality) : createNavigatorBatchedMeshSet(quality);

        const mapsToClear = isMainCanvas
            ? [
                  state.articleGeometrySlots,
                  state.instanceBatchIds,
                  state.physicalBatchToInstance,
                  state.sheetBatchToInstance,
                  state.holesBatchToInstance
              ]
            : [state.navigatorGeometrySlots, state.navigatorInstanceBatchIds];

        for (const map of mapsToClear) {
            map.clear();
        }
        setMeshes(meshSet.physical, meshSet.sheet, meshSet.holes);

        return () => {
            meshSet.physical?.dispose();
            meshSet.sheet?.dispose();
            meshSet.holes?.dispose();

            setMeshes(null, null, null);

            const currentState = usePlacementStore.getState();
            const currentMapsToClear = isMainCanvas
                ? [
                      currentState.articleGeometrySlots,
                      currentState.instanceBatchIds,
                      currentState.physicalBatchToInstance,
                      currentState.sheetBatchToInstance,
                      currentState.holesBatchToInstance
                  ]
                : [currentState.navigatorGeometrySlots, currentState.navigatorInstanceBatchIds];

            for (const map of currentMapsToClear) {
                map.clear();
            }
        };
    }, [isMainCanvas, setMeshes]);
};
